import { Skeleton } from '@mui/material';
import { cn, PdfViewer } from '@thingslog/ui-components';
import React, { forwardRef, useEffect, useState } from 'react';
import type { PdfScaleMode, PdfViewerRef } from '@thingslog/ui-components';

interface PdfProps {
  documentUrl: string;
  scaleMode?: PdfScaleMode;
  onLoad?: (totalPages: number) => void;
  onPageChange?: (page: number, totalPages: number) => void;
  onZoomChange?: (zoom: number) => void;
  className?: string;
  viewerClassName?: string;
}

export const Pdf = forwardRef<PdfViewerRef, PdfProps>(function Pdf(
  {
    documentUrl,
    scaleMode,
    onLoad,
    onPageChange,
    onZoomChange,
    className,
    viewerClassName,
  }: PdfProps,
  ref: React.ForwardedRef<PdfViewerRef>
) {
  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect((): void => {
    setIsPdfLoading(true);
    setError(null);
  }, [documentUrl]);

  if (error) {
    return (
      <div className={cn('text-red-500', className)} data-testid="pdf-viewer-error">
        {error}
      </div>
    );
  }

  return (
    <div
      data-testid="pdf-viewer"
      data-loading={isPdfLoading}
      className={cn('h-full w-full', className)}
    >
      <Skeleton
        variant="rectangular"
        className={cn('h-full w-full', !isPdfLoading && 'hidden')}
        data-testid="pdf-viewer-skeleton"
      />
      <PdfViewer
        ref={ref}
        src={documentUrl}
        scaleMode={scaleMode}
        transitionDuration={500}
        onLoad={(totalPages: number): void => {
          setIsPdfLoading(false);
          onLoad?.(totalPages);
        }}
        onPageChange={onPageChange}
        onZoomChange={onZoomChange}
        onError={(viewerError: Error): void => {
          setIsPdfLoading(false);
          setError(viewerError.message);
        }}
        className={cn('h-full w-full', isPdfLoading && 'hidden', viewerClassName)}
      />
    </div>
  );
});

Pdf.displayName = 'Pdf';
